import { type ISearchFilterRef } from '@/components';
import {
	MasterInfoEnum,
	WindowPostActionType,
	WindowPostMessage,
} from '@/constant';
import { ApiResponseEnum } from '@/services';
import { CommonActions } from '@/stores';
import { navigateCreateOrDetail, showToast } from '@/utils';
import { RefObject, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { isNilOrEmpty } from 'ramda-adjunct';

type Params = {
	toDetailPathName?: string | number;
	searchFilterRef?: RefObject<ISearchFilterRef>;
	callbackAfterClosePopup?: (data?: any) => void;
};

export const useDetailListSubmit = (params: Params) => {
	const { toDetailPathName, searchFilterRef, callbackAfterClosePopup } = params;
	const dispatch = useDispatch();

	const { t } = useTranslation('message');

	const onDetailSubmit = useCallback(
		(id: string | number, pathName?: string, query?: any) => {
			if (isNilOrEmpty(toDetailPathName) && isNilOrEmpty(pathName)) return;
			const popup = navigateCreateOrDetail(
				(toDetailPathName?.toString() as string) || (pathName as string),
				id,
				query
			);

			let needToReloadListAfterClosePopup = false;
			let needToCallbackAfterClosePopup = false;
			let caseNeedToReloadListAfterClosePopup = '';
			let data: any;

			if (popup) {
				popup.addEventListener(
					'message',
					(event) => {
						if (/^react-devtools/gi.test(event?.data?.source)) {
							return;
						}
						const message = event?.data?.message || event?.data;

						switch (message) {
							case ApiResponseEnum.SUCCESS:
							case WindowPostMessage.DELETE_SUCCESS:
								popup.close();

								searchFilterRef?.current?.onSearchChange?.(1);
								break;
							case WindowPostMessage.RELOAD_LIST_BUT_DO_NOT_CLOSE_POPUP:
								searchFilterRef?.current?.onSearchChange?.(1);
								break;
							case WindowPostMessage.NEED_TO_RELOAD_LIST_AFTER_CLOSE_POPUP:
								needToReloadListAfterClosePopup = true;
								break;
							case WindowPostMessage.NEED_TO_CALLBACK_AFTER_CLOSE_POPUP:
								needToCallbackAfterClosePopup = true;
								break;
							default:
								break;
						}

						const action = event?.data?.action;

						data = event?.data?.values;

						switch (action) {
							case WindowPostActionType.RELOAD_PUBLIC_FRANCHISE_AGRICULTURE:
								dispatch(
									CommonActions.getListCommonRequest({
										type: MasterInfoEnum.PUBLIC_FRANCHISE_AGRICULTURE,
										params: {},
									})
								);
								break;
							case WindowPostActionType.RELOAD_HEADER_PROGRAM:
								caseNeedToReloadListAfterClosePopup =
									WindowPostActionType.RELOAD_HEADER_PROGRAM;
								break;
							case WindowPostActionType.RESET_HEADER_PROGRAM:
								dispatch(CommonActions.resetHeaderProgram());
								break;
							case WindowPostActionType.RELOAD_PROGRAM_STATISTIC:
								caseNeedToReloadListAfterClosePopup =
									WindowPostActionType.RELOAD_PROGRAM_STATISTIC;
								break;
							case WindowPostActionType.RELOAD_INQUIRY_BOARD_ANSWER_AFTER_DELETE_ANSWER:
								needToReloadListAfterClosePopup = true;
								break;
							case WindowPostActionType.RELOAD_ASSIGNMENT_CALENDAR:
								caseNeedToReloadListAfterClosePopup =
									WindowPostActionType.RELOAD_ASSIGNMENT_CALENDAR;
								break;
							default:
								break;
						}

						if (message === WindowPostMessage.DELETE_SUCCESS) {
							showToast(t('DELETE_SUCCESS'));
						}
					},
					false
				);
			}

			let timer = setInterval(() => {
				if (popup?.closed) {
					if (needToReloadListAfterClosePopup) {
						searchFilterRef?.current?.onSearchChange?.(1);
					}

					if (needToCallbackAfterClosePopup) {
						callbackAfterClosePopup?.(data);
					}

					switch (caseNeedToReloadListAfterClosePopup) {
						case WindowPostActionType.RELOAD_HEADER_PROGRAM:
							dispatch(
								CommonActions.getListCommonRequest({
									type: MasterInfoEnum.PROGRAM,
									params: {},
								})
							);
							break;

						case WindowPostActionType.RELOAD_PROGRAM_STATISTIC:
							callbackAfterClosePopup?.();
							break;
						case WindowPostActionType.RELOAD_ASSIGNMENT_CALENDAR:
							callbackAfterClosePopup?.(data);
							break;
						default:
							break;
					}

					clearInterval(timer);
					window.removeEventListener('message', () => {});
				}
			}, 1000);
		},
		[toDetailPathName, searchFilterRef, callbackAfterClosePopup]
	);

	return {
		onDetailSubmit,
	};
};
